import { useCallback, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Switch from '@mui/material/Switch';
import { transparentize } from 'color2k';
import { Colors, TextCss } from 'styles/index';
import Text from 'common/ui/Text';
import { Stack } from 'common/ui/Layout';
import { WorkspaceLayoutPreview } from './WorkspaceLayoutPreview';
import type { WorkspaceLayoutPreset } from 'generated/graphql';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import Input from 'common/ui/Input';
import InfoIcon from '@mui/icons-material/Info';
import { Button } from 'common/ui/Button';
import { useMutation } from '@apollo/client';
import {
  CREATE_WORKSPACE_LAYOUT_PRESET,
  GET_ME,
  UPDATE_WORKSPACE_LAYOUT_PRESET,
} from 'modules/Apollo/queries';
import { telemetryContextFor } from 'modules/Apollo/types';
import { logger } from 'modules/logger';
import { useCurrentUser } from 'hooks/useCurrentUser';
import assocPath from 'ramda/es/assocPath';
import { NameSpaces, CrossWindowDataRefetchType, sendCrossTabEvent } from 'modules/EventsManager';
import { useRecoilValue } from 'recoil';
import { workspaceDisplayArrangementState } from '../state';
import { buildDisplayArrangementKeyFromDisplaySlots } from '../utils';
import { useWorkspaceLayoutManager } from '../useWorkspaceLayoutManager';

type WorkspaceLayoutModalMode = 'create' | 'edit' | 'clone';

type WorkspaceLayoutModalProps = {
  open: boolean;
  mode: WorkspaceLayoutModalMode;
  workspaceLayoutPreset: Pick<
    WorkspaceLayoutPreset,
    'id' | 'name' | 'displayArrangementKey' | 'displaySlots' | 'windows' | 'isDefault'
  >;
  onClose: () => void;
  onDelete?: () => void;
};

const sectionCardSx = {
  border: `1px solid ${transparentize(Colors.gray5, 0.35)}`,
  borderRadius: 2,
  backgroundColor: Colors.gray1,
};

const notifyWorkspaceLayoutPresetsChanged = () => {
  sendCrossTabEvent(NameSpaces.CROSS_WINDOW_DATA_REFETCH, {
    type: CrossWindowDataRefetchType.USER,
  });
};

export const WorkspaceLayoutModal = ({
  open,
  mode,
  workspaceLayoutPreset,
  onClose,
  onDelete,
}: WorkspaceLayoutModalProps) => {
  const { data: meData } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const { selectWorkspaceLayoutPreset } = useWorkspaceLayoutManager();
  const currentDisplayArrangement = useRecoilValue(workspaceDisplayArrangementState);
  const initialName = useMemo(() => {
    if (mode !== 'clone') {
      return workspaceLayoutPreset.name ?? '';
    }

    const baseName = workspaceLayoutPreset.name?.trim();
    return baseName ? `${baseName} Copy` : 'Copy';
  }, [mode, workspaceLayoutPreset.name]);

  const [name, setName] = useState(initialName);
  const [shouldAutoApply, setShouldAutoApply] = useState(
    mode === 'clone' ? false : (workspaceLayoutPreset.isDefault ?? false)
  );
  const currentWorkspaceLayoutPreset = useRecoilValue(workspaceDisplayArrangementState);
  const isCreate = mode === 'create' || mode === 'clone';

  const isMatchingDisplayArrangement = useMemo(() => {
    const currentDisplayArrangementKey = buildDisplayArrangementKeyFromDisplaySlots(
      currentDisplayArrangement?.displays ?? []
    );
    const presetDisplayArrangementKey = buildDisplayArrangementKeyFromDisplaySlots([
      ...workspaceLayoutPreset.displaySlots,
    ]);
    return isCreate || currentDisplayArrangementKey === presetDisplayArrangementKey;
  }, [currentDisplayArrangement?.displays, workspaceLayoutPreset.displaySlots, isCreate]);

  const [createWorkspaceLayoutPreset, { loading: isCreating }] = useMutation(
    CREATE_WORKSPACE_LAYOUT_PRESET,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );

  const [updateWorkspaceLayoutPreset, { loading: isEditing }] = useMutation(
    UPDATE_WORKSPACE_LAYOUT_PRESET,
    {
      context: APOLLO_QUERY_CONTEXT,
    }
  );

  const onSave = useCallback(async () => {
    const payload = {
      name,
      isDefault: shouldAutoApply,
      displayArrangementKey: workspaceLayoutPreset.displayArrangementKey,
      displaySlots: workspaceLayoutPreset.displaySlots,
      windows: workspaceLayoutPreset.windows,
    };

    if (isCreate) {
      await createWorkspaceLayoutPreset({
        variables: {
          payload,
        },
        onCompleted: (data) => {
          const id = data?.createWorkspaceLayoutPreset?.id;
          const createdPreset = data?.createWorkspaceLayoutPreset?.presets.find(
            (preset: WorkspaceLayoutPreset) => preset.id === id
          );

          const name = createdPreset?.name;

          if (
            shouldAutoApply &&
            isMatchingDisplayArrangement &&
            createdPreset?.id != null &&
            createdPreset.name != null &&
            createdPreset.windows != null &&
            createdPreset.displayArrangementKey != null
          ) {
            selectWorkspaceLayoutPreset({
              id: createdPreset.id,
              name: createdPreset.name,
              windows: createdPreset.windows,
              displayArrangementKey: createdPreset.displayArrangementKey,
            });
          }

          logger.info(
            `[WorkspaceLayoutModal] Workspace layout preset with id ${id} and name ${name} created successfully`,
            { id, name }
          );

          notifyWorkspaceLayoutPresetsChanged();
          onClose();
        },
        update: (proxy, { data }) => {
          proxy.writeQuery({
            query: GET_ME,
            data: assocPath(
              ['me', 'workspaceSettings', 'layoutSettings', 'presets'],
              data?.createWorkspaceLayoutPreset?.presets ?? [],
              meData
            ),
          });
        },
      });
    } else {
      await updateWorkspaceLayoutPreset({
        variables: {
          id: workspaceLayoutPreset.id,
          payload,
        },
        onCompleted: (data) => {
          const updatedPreset = data?.updateWorkspaceLayoutPreset?.presets.find(
            (preset: WorkspaceLayoutPreset) => preset.id === workspaceLayoutPreset.id
          );
          const name = updatedPreset?.name;

          if (
            shouldAutoApply &&
            isMatchingDisplayArrangement &&
            updatedPreset?.id != null &&
            updatedPreset.name != null &&
            updatedPreset.windows != null &&
            updatedPreset.displayArrangementKey != null
          ) {
            selectWorkspaceLayoutPreset({
              id: updatedPreset.id,
              name: updatedPreset.name,
              windows: updatedPreset.windows,
              displayArrangementKey: updatedPreset.displayArrangementKey,
            });
          }

          logger.info(
            `[WorkspaceLayoutModal] Workspace layout preset with id ${workspaceLayoutPreset.id} and name ${name} updated successfully`,
            { id: workspaceLayoutPreset.id, name }
          );

          notifyWorkspaceLayoutPresetsChanged();
          onClose();
        },
        update: (proxy, { data }) => {
          if (!meData) {
            return;
          }

          proxy.writeQuery({
            query: GET_ME,
            data: assocPath(
              ['me', 'workspaceSettings', 'layoutSettings', 'presets'],
              data?.updateWorkspaceLayoutPreset?.presets ?? [],
              meData
            ),
          });
        },
      });
    }
  }, [
    name,
    shouldAutoApply,
    workspaceLayoutPreset.displayArrangementKey,
    workspaceLayoutPreset.displaySlots,
    workspaceLayoutPreset.windows,
    workspaceLayoutPreset.id,
    isCreate,
    createWorkspaceLayoutPreset,
    onClose,
    meData,
    updateWorkspaceLayoutPreset,
    isMatchingDisplayArrangement,
    selectWorkspaceLayoutPreset,
  ]);

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            width: 760,
            borderRadius: 2,
            backgroundColor: Colors.gray2,
            padding: 2,
          },
        },
      }}
    >
      <DialogTitle>
        <Stack alignY="center" alignX="between">
          <Stack vertical>
            <Text variant="display2">
              {isCreate ? 'Create Workspace Layout' : 'Edit Workspace Layout'}
            </Text>
          </Stack>
          <IconButton
            onClick={() => {
              onClose();
            }}
            css="transform: translateX(12px)"
            size="large"
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack vertical space="medium">
          <Stack vertical space="xsmall">
            <Text color="secondary-alt" variant="body1" css="margin-left: 8px">
              Layout Name
            </Text>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g Home Reading"
              borderColor={Colors.gray8}
              css={`
                border: 1px solid var(--field-border, #42505a);
                background: var(--field-bg, #000a12);
              `}
            />
          </Stack>
          <Box sx={{ ...sectionCardSx, p: 2, flexShrink: 0 }}>
            <WorkspaceLayoutPreview workspaceLayoutPreset={workspaceLayoutPreset} />
          </Box>

          <Box
            sx={{
              ...sectionCardSx,
              px: 2,
              py: 1.5,
            }}
            data-testid="default-layout-section"
          >
            <Stack vertical>
              <Stack alignX="between" alignY="center">
                <Stack alignY="center" space="xsmall">
                  <Tooltip
                    title={
                      <Text variant="body2">
                        A workstation setup is determined by the displays connected to your
                        computer, including their arrangement and orientation. Sirona automatically
                        detects workstation setups and applies the appropriate default layout.
                      </Text>
                    }
                  >
                    <span
                      css={`
                        display: flex;
                        align-items: center;
                      `}
                    >
                      <InfoIcon
                        css={`
                          ${TextCss.display2}
                          color: ${Colors.gray8};
                          margin-left: 2px;
                        `}
                      />
                    </span>
                  </Tooltip>
                  <Stack vertical>
                    <Stack alignY="center" space="xxsmall">
                      <Text css="font-weight: 500;">
                        Make this the default layout for{' '}
                        {isMatchingDisplayArrangement
                          ? 'this workstation'
                          : 'the workstation shown above'}
                      </Text>
                    </Stack>
                    <Text color="secondary-alt" variant="caption">
                      Automatically use this layout whenever{' '}
                      {isMatchingDisplayArrangement
                        ? 'this workstation setup is detected'
                        : 'the above workstation setup is detected'}
                    </Text>
                  </Stack>
                </Stack>
                <Switch
                  checked={shouldAutoApply}
                  onChange={(e) => setShouldAutoApply(e.target.checked)}
                />
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 4 }}>
        {!isCreate && (
          <Button onClick={onDelete} variant="ghost-error" useDefaultPadding={false}>
            Delete
          </Button>
        )}
        <Button
          loading={isCreating || isEditing}
          disabled={name.trim() === '' || isCreating || isEditing}
          useDefaultPadding={false}
          onClick={async () => {
            logger.info(`[WorkspaceLayoutModal] ${isCreate ? 'Create' : 'Save'} button clicked`, {
              name,
              shouldAutoApply,
            });
            await onSave();
          }}
        >
          {isCreate ? (isCreating ? 'Creating...' : 'Create') : isEditing ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

WorkspaceLayoutModal.displayName = 'WorkspaceLayoutModal';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(WorkspaceLayoutModal);
